/** Édition du fuseau utilisateur (« Manœuvre libre », docs/14-manoeuvre-libre.md §2.3, §2.4, §3.1, §5.4).
 *
 *  Fonctions pures sur le modèle stocké (`exercise.placement.corridor`, limites en [lon, lat]) : glissements
 *  des sommets (paire liée ou sommet seul, aimantation à la largeur par défaut de la clé de gabarit, docs/15
 *  §2.4, décalage latéral pur),
 *  insertion et retrait de paires, glissement, inclinaison et courbure des LIMA avec butée sur leurs voisines,
 *  ajout et retrait des LIMA, GDH d'aperçu et contrôle `validate`, qui reprend exactement
 *  `generex_engine.user_corridor.validate` (mêmes étapes, mêmes codes et messages, jeu de vecteurs partagé
 *  `__fixtures__/validate.json`). Aucune fonction ne modifie son argument : chacune rend un nouveau fuseau
 *  (ou le même objet quand rien ne change). Le serveur fait foi au relâcher. */

import type { Corridor, CorridorLineModel, CorridorModel, PhaseLineName } from "../../api/types";
import { PLACED_LEVELS, type PlacedLevel } from "../../echelons";
import { bearingDeg, moveM } from "../geo";
import {
  CorridorFrame,
  frameOf,
  fromLocal,
  helperDistance,
  planeOf,
  toLocal,
  type LonLat,
  type Plane,
} from "./corridorFrame";
import {
  alertPairs,
  forLevel,
  GEOMETRY_SCALE_FLOOR_M,
  keyed,
  MODIFIED_RATIO,
  retouched,
  type WidthDoctrine,
  widthWarning,
  withinHardBounds,
  worstWidth,
} from "./corridorWidths";
import { douglasPeucker, polygonDistance, polylinesIntersect, ringFault, selfFault, type XY } from "./planar";

export type { LonLat };

// --- Modèle (§3.1) ------------------------------------------------------------------------------------------

export type LineName = PhaseLineName;
export const LINE_NAMES: readonly LineName[] = ["L0", "L1", "L2", "L3"];
export type CorridorLevel = PlacedLevel;
export const LEVELS: readonly CorridorLevel[] = PLACED_LEVELS;
export type Side = "left" | "right";

/** LIMA dans le repère (s, t) : extrémités sur la limite gauche (t = −1) et droite (t = +1), courbures. Alias du
 *  type de l'API (`CorridorLineModel`, api/types.ts), source unique du modèle. */
export type UserLine = CorridorLineModel;

/** Fuseau utilisateur tel que stocké : limites géographiques gauche et droite face à l'ENI, de l'arrière vers
 *  l'ennemi, en [lon, lat], même nombre de sommets (paire i = (left[i], right[i])) ; `seed` et `prior_lines`
 *  (camp) sont calculés par le serveur et ignorés à l'entrée. Alias du type de l'API (`CorridorModel`,
 *  api/types.ts), source unique du modèle. */
export type UserCorridor = CorridorModel;

/** Fuseau lu par `parseCorridor` : les noms de LIMA ne sont pas encore contrôlés ; `seed_basis` (base de
 *  l'amorce, « web » : largeurs du mémento ; null : fuseau antérieur) décide de la règle d'alerte de largeur
 *  quand le fuseau porte son amorce (forme stockée du serveur, jeu de vecteurs). */
export type ParsedCorridor = Omit<UserCorridor, "lines"> & { lines: Record<string, UserLine>; seed_basis: "web" | null };

/** Emprise (ouest, sud, est, nord), en degrés. */
export type Bounds = readonly [number, number, number, number];

// --- Constantes (user_corridor, mapdraw, dtg, geometry) -----------------------------------------------------

export const VERSION = 1;
export const MIN_PAIRS = 2;
export const MAX_PAIRS = 64;
export const MAX_LINES = 4;
export const MAX_BENDS = 14;
export const MAX_BEND_T = 0.98;
export const MIN_HOURS = 1;
export const MAX_HOURS = 400;
export const MIN_AXIS_M = 5_000;
/** Axe minimal propre à un niveau (compagnie : 2 km, docs/21 §3) ; les autres niveaux gardent 5 km. */
export const MIN_AXIS_BY_LEVEL: Readonly<Partial<Record<string, number>>> = { COMPAGNIE: 2_000, CORPS: 100_000 };
export const MAX_AXIS_M = 200_000;
/** Axe maximal propre à un niveau (corps d'armée : 800 km, profondeur maximale du mémento ; docs/08 §23). */
export const MAX_AXIS_BY_LEVEL: Readonly<Partial<Record<string, number>>> = { CORPS: 800_000 };
export const MAX_TURN_DEG = 90;
// Virages, orientation et tracé des LIMA se mesurent à l'échelle W' = max(largeur moyenne ; 18 150 m) (docs/15
// §2.4, `GEOMETRY_SCALE_FLOOR_M`) : les amorces de GTIA d'infanterie (8 km) restent valides.
const TURN_SIMPLIFY_RATIO = 0.05; // axe médian simplifié (Douglas-Peucker, 0,05 W') avant les virages
const ORIENT_HALF_RATIO = 0.5; // direction médiane de 0,5 W' en arrière à 0,5 W' en avant
const LINE_INSIDE_RATIO = 0.02; // une LIMA peut déborder de 0,02 W'
const LINE_INSIDE_SAMPLES = 32; // points contrôlés par segment de LIMA (extrémités comprises : 33)
const MIN_CENTER_STEP_M = 0.1; // deux paires consécutives de même milieu : contour dégénéré
export const LINE_ORDER_GAP_M = 50; // deux LIMA, sur chaque limite (mapdraw.LINE_ORDER_GAP_M)
export const WORLD: Bounds = [-179.5, -79.5, 179.5, 83.5];
const HOURS_PER_AXIS = 10; // mapdraw.DTG_HOURS_PER_LENGTH : 10 h pour toute la longueur de l'axe
export const LINE_OFFSETS_H: Record<LineName, number> = { L0: 6, L1: 12, L2: 24, L3: 48 };
/** Durée de la mission par niveau, de L0 à L3 (`dtg.MISSION_HOURS`, décision D13 du 23/10/2026 : Mémento
 *  tactique EDG p. 13 ; le S/GTIA garde les LIMA de son GTIA). */
export const MISSION_HOURS: Record<string, number> = { COMPAGNIE: 12, REGIMENT: 12, BRIGADE: 36, DIVISION: 108, CORPS: 324 };
const PHASE_SHARE: Record<Exclude<LineName, "L0">, number> = { L1: 1 / 7, L2: 3 / 7, L3: 1 };

/** Heures des LIMA sans glissement au niveau du fuseau (`user_corridor.line_bases`) : L0 = H (début + 6 h),
 *  L1 à L3 à H + la durée du niveau × 1/7, 3/7, 1, arrondies à l'heure ; table de l'iPad sans niveau connu. */
export function lineBases(level?: string | null): Record<LineName, number> {
  const duration = MISSION_HOURS[String(level ?? "").toUpperCase()];
  if (duration === undefined) return { ...LINE_OFFSETS_H };
  const h = LINE_OFFSETS_H.L0;
  const at = (share: number) => h + Math.floor(duration * share + 0.5);
  return { L0: h, L1: at(PHASE_SHARE.L1), L2: at(PHASE_SHARE.L2), L3: at(PHASE_SHARE.L3) };
}
export const MARGIN_M = 500; // prolongement des LIMA au-delà des limites (mapdraw.MARGIN_M)
/** Aimantation : à moins de 3 % de la largeur par défaut de la clé de gabarit (médiane du mémento), la largeur
 *  s'y cale exactement (docs/14 §2.3, docs/15 §2.4). */
export const SNAP_RATIO = 0.03;
/** Une paire est « modifiée » quand sa largeur s'écarte de plus de 1 % de celle de l'amorce (§2.2). */
export { MODIFIED_RATIO };
/** Paire de l'amorce reconnue telle quelle : ses deux sommets à moins de 1 m (`user_corridor.SAME_PAIR_M`). */
export const SAME_PAIR_M = 1;
/** Butée d'une LIMA : arrêt à 1 cm au-delà de l'écart de 50 m (le serveur refuse l'écart pile). */
const STOP_MARGIN_M = 0.01;
/** `mapdraw.order_violation` : un mouvement de plus d'1 m qui éloigne une ligne trop proche reste permis. */
const ESCAPE_M = 1;

/** Largeur par défaut du fuseau (médiane du mémento) pour la clé de gabarit, union des familles du niveau
 *  sans clé ; NaN pour un niveau sans fuseau doctrinal (CENTAC). */
export function defaultWidth(level: string, levelKey?: string | null): number {
  return forLevel(level, levelKey)?.default_m ?? NaN;
}

// --- Règles de validation (§3.1) ------------------------------------------------------------------------------

/** Code de règle → message générique du serveur (F21, traduit côté client par `tServer`). */
export const RULES = {
  format: "Fuseau refusé : format illisible",
  version: "Fuseau refusé : version du format inconnue",
  level: "Fuseau refusé : niveau différent de celui du dossier",
  pairs_count: "Fuseau refusé : de 2 à 64 paires de sommets",
  pairs_mismatch: "Fuseau refusé : les deux limites n'ont pas le même nombre de sommets",
  finite: "Fuseau refusé : coordonnée non finie",
  lines_count: "Fuseau refusé : de 1 à 4 LIMA",
  line_name: "Fuseau refusé : LIMA inconnue (L0 à L3)",
  l0_missing: "Fuseau refusé : la LIMA L0 est obligatoire",
  ends_range: "Fuseau refusé : extrémité de LIMA hors du fuseau",
  bends_count: "Fuseau refusé : 14 courbures au plus par LIMA",
  bend_range: "Fuseau refusé : courbure de LIMA hors du fuseau",
  default_s_range: "Fuseau refusé : position par défaut de LIMA hors du fuseau",
  hours_range: "Fuseau refusé : GDH de LIMA entre 1 h et 400 h après le début de l'exercice",
  world: "Fuseau refusé : hors des latitudes et longitudes admises",
  antimeridian: "Fuseau refusé : segment de plus de 180° de longitude",
  bounds: "Fuseau refusé : hors de la zone",
  axis_length: "Fuseau refusé : axe de 5 à 200 km",
  degenerate: "Fuseau refusé : deux paires de sommets confondues",
  orientation: "Fuseau refusé : limites gauche et droite inversées par endroits",
  crossing: "Fuseau refusé : les limites se croisent",
  polygon: "Fuseau refusé : contour du fuseau invalide",
  turn: "Fuseau refusé : virage de plus de 90°",
  width: "Fuseau refusé : largeur hors des bornes admises",
  line_order: "Fuseau refusé : ordre des LIMA L0 < L1 < L2 < L3 non respecté",
  line_crossing: "Fuseau refusé : deux LIMA se croisent",
  line_inside: "Fuseau refusé : LIMA hors du fuseau",
  hours_order: "Fuseau refusé : GDH des LIMA non croissants",
} as const;
export type RuleCode = keyof typeof RULES;

/** Étapes : la structure et l'emprise relèvent toutes leurs fautes ; la géométrie des limites, puis celle
 *  des LIMA, s'arrêtent à la première règle violée. */
export const STAGES = {
  structure: [
    "version", "level", "pairs_count", "pairs_mismatch", "finite", "lines_count", "line_name", "l0_missing",
    "ends_range", "bends_count", "bend_range", "default_s_range", "hours_range",
  ],
  place: ["world", "antimeridian", "bounds"],
  rails: ["axis_length", "degenerate", "orientation", "crossing", "polygon", "turn", "width"],
  lines: ["line_order", "line_crossing", "line_inside", "hours_order"],
} as const satisfies Record<string, readonly RuleCode[]>; // prettier-ignore

/** Résultat du contrôle : codes et messages du serveur, avertissements, et pour l'affichage les paires et
 *  LIMA en faute (segment fautif en tirets rouges, §2.3). */
export interface CorridorCheck {
  rules: RuleCode[];
  errors: string[];
  warnings: string[];
  /** Indices des paires en cause (un segment de limite i → i + 1 donne les paires i et i + 1). */
  pairs: number[];
  /** LIMA en cause. */
  lines: LineName[];
}

// --- Lecture tolérante (user_corridor.parse) -------------------------------------------------------------------

const isNumber = (value: unknown): value is number => typeof value === "number";
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

function readPoints(raw: unknown): LonLat[] | null {
  if (!Array.isArray(raw)) return null;
  const out: LonLat[] = [];
  for (const item of raw) {
    if (!Array.isArray(item) || item.length < 2) return null;
    const [lon, lat] = item as unknown[];
    if (!isNumber(lon) || !isNumber(lat)) return null;
    out.push([lon, lat]);
  }
  return out;
}

/** Valeur fausse au sens de Python (`x or []`) : absente, null, false, 0, chaîne, liste ou objet vides. */
function pyFalsy(value: unknown): boolean {
  if (value === undefined || value === null || value === false || value === 0 || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  return isRecord(value) && Object.keys(value).length === 0;
}

function readLine(raw: unknown): UserLine | null {
  if (!isRecord(raw)) return null;
  const ends = raw.ends;
  if (!Array.isArray(ends) || ends.length !== 2) return null;
  const [sG, sD] = ends as unknown[];
  if (!isNumber(sG) || !isNumber(sD)) return null;
  const bends: [number, number][] = [];
  // `raw.get("bends") or []` : une valeur vide au sens de Python vaut aucune courbure, toute autre valeur non
  // liste est illisible.
  if (!pyFalsy(raw.bends) && !Array.isArray(raw.bends)) return null;
  for (const item of Array.isArray(raw.bends) ? raw.bends : []) {
    if (!Array.isArray(item) || item.length !== 2) return null;
    const [s, t] = item as unknown[];
    if (!isNumber(s) || !isNumber(t)) return null;
    bends.push([s, t]);
  }
  const hours = raw.hours ?? null;
  if (hours !== null && !isNumber(hours)) return null;
  return {
    ends: [sG, sD],
    bends,
    default_s: isNumber(raw.default_s) ? raw.default_s : (sG + sD) / 2,
    hours,
    moved: raw.moved === true,
  };
}

/** Lecture tolérante d'un fuseau (forme stockée) ; null si la structure est inutilisable (types, couples de
 *  coordonnées) ; les valeurs hors plafonds sont gardées pour que `validate` nomme la règle violée. */
export function parseCorridor(raw: unknown): ParsedCorridor | null {
  if (!isRecord(raw)) return null;
  const left = readPoints(raw.left);
  const right = readPoints(raw.right);
  if (!left || !right || !isRecord(raw.lines)) return null;
  // Sans prototype : une clé « __proto__ » reste une clé ordinaire et tombe sous `line_name`, comme au serveur.
  const lines: Record<string, UserLine> = Object.create(null);
  for (const [name, entry] of Object.entries(raw.lines)) {
    const line = readLine(entry);
    if (!line) return null;
    lines[name] = line;
  }
  const version = "v" in raw ? raw.v : VERSION;
  const seedRaw = isRecord(raw.seed) ? raw.seed : null;
  const seedLeft = seedRaw ? readPoints(seedRaw.left) : null;
  const seedRight = seedRaw ? readPoints(seedRaw.right) : null;
  const seed =
    seedLeft && seedRight && seedLeft.length === seedRight.length && seedLeft.length >= 2
      ? { left: seedLeft, right: seedRight }
      : null;
  // `seed_basis` n'a de sens qu'avec une amorce lisible (`user_corridor.parse`).
  const seedBasis = seed && raw.seed_basis === "web" ? "web" : null;
  return {
    v: Number.isInteger(version) ? (version as number) : -1,
    origin: raw.origin ? String(raw.origin) : "auto",
    level: raw.level ? String(raw.level) : "",
    left,
    right,
    lines,
    seed,
    prior_lines: isRecord(raw.prior_lines)
      ? Object.fromEntries(Object.entries(raw.prior_lines).filter((e): e is [string, number] => isNumber(e[1])))
      : null,
    seed_basis: seedBasis,
  };
}

// --- Outils ----------------------------------------------------------------------------------------------------

const clamp = (x: number, low: number, high: number) => Math.min(Math.max(x, low), high);

/** LIMA présentes, dans l'ordre L0 → L3. */
export function presentLines(c: { lines: Partial<Record<string, UserLine>> }): LineName[] {
  return LINE_NAMES.filter((name) => c.lines[name] !== undefined);
}

const sRef = (line: UserLine) => (line.ends[0] + line.ends[1]) / 2;

/** Lignes réordonnées L0 → L3 (ordre des clés du modèle stocké). */
function ordered(lines: Partial<Record<LineName, UserLine>>): Partial<Record<LineName, UserLine>> {
  const out: Partial<Record<LineName, UserLine>> = {};
  for (const name of LINE_NAMES) if (lines[name]) out[name] = lines[name];
  return out;
}

function withLine(c: UserCorridor, name: LineName, line: UserLine | null): UserCorridor {
  const lines = { ...c.lines };
  if (line) lines[name] = line;
  else delete lines[name];
  return { ...c, lines: ordered(lines) };
}

/** Largeur de chaque paire (m). */
export function widths(c: { left: readonly LonLat[]; right: readonly LonLat[] }): number[] {
  const n = Math.min(c.left.length, c.right.length);
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(helperDistance(c.left[i]!, c.right[i]!));
  return out;
}

/** Point ramené dans l'emprise. */
export function clampToBounds(p: LonLat, bounds: Bounds | null | undefined): LonLat {
  if (!bounds) return p;
  return [clamp(p[0], bounds[0], bounds[2]), clamp(p[1], bounds[1], bounds[3])];
}

/** `mapdraw.round_hours` : arrondi à l'heure à la manière de Swift (moitiés loin de zéro). */
export function roundHours(hours: number): number {
  return hours >= 0 ? Math.floor(hours + 0.5) : -Math.floor(-hours + 0.5);
}

// --- Glissement des sommets (§2.3) ---------------------------------------------------------------------------

/** Direction locale de l'axe médian à la paire i (milieux voisins), dans le plan donné ; unitaire. */
function axisDirection(c: UserCorridor, index: number, plane: Plane): [number, number] {
  const n = c.left.length;
  const center = (i: number) =>
    toLocal([(c.left[i]![0] + c.right[i]![0]) / 2, (c.left[i]![1] + c.right[i]![1]) / 2], plane);
  const [ax, ay] = center(Math.max(index - 1, 0));
  const [bx, by] = center(Math.min(index + 1, n - 1));
  const norm = Math.hypot(bx - ax, by - ay);
  if (norm > 0) return [(bx - ax) / norm, (by - ay) / norm];
  // Paires confondues : perpendiculaire à la paire.
  const [gx, gy] = toLocal(c.left[index]!, plane);
  const [dx, dy] = toLocal(c.right[index]!, plane);
  const width = Math.hypot(dx - gx, dy - gy) || 1;
  return [-(dy - gy) / width, (dx - gx) / width];
}

/** Perpendiculaire locale de l'axe à la paire i (unitaire, vers la droite géographique). */
function lateralDirection(c: UserCorridor, index: number, plane: Plane): [number, number] {
  const [ux, uy] = axisDirection(c, index, plane);
  return [uy, -ux];
}

function vertex(c: UserCorridor, side: Side, index: number): LonLat {
  return (side === "left" ? c.left : c.right)[index]!;
}

export interface MoveOptions {
  /** ⇧ : mouvement limité à la perpendiculaire locale de l'axe (décalage latéral pur). */
  lateral?: boolean;
  /** Emprise admise : on bute contre son contour. */
  bounds?: Bounds | null;
  /** Sommets de la limite saisie affichés en permanence (poignées majeures, `handles.majorVertices` au zoom
   *  courant) : l'influence du glissement s'étend au moins jusqu'aux voisins affichés, qui restent en place. */
  anchors?: Iterable<number> | null;
}

// --- Déformation douce (§2.3) --------------------------------------------------------------------------------
//
// L'amorce automatique est calée sur le terrain : ses limites portent des sommets quasi confondus (12 m, 13 m,
// 103 m, 142 m, 193 m après un sommet principal). Un glissement rigide d'un seul sommet y dresse une pointe qui
// recoupe le petit segment voisin (« les limites se croisent ») ou fait un virage de plus de 90° : seul un
// mouvement d'une centaine de mètres passait. Le sommet saisi entraîne donc son voisinage : le sommet j de la
// même limite suit de w(d)·Δ, d étant la distance le long de la limite (amorce du geste) et w un cosinus
// surélevé, 1 au sommet saisi, 0 au rayon R et au-delà, de dérivée nulle aux deux bouts (aucun pli).
//
// Rayon, de chaque côté du sommet saisi : R = max(1 500 m, 2 |Δ|, distance au voisin affiché le plus proche).
// - 1 500 m : les sommets quasi confondus (moins de 200 m) bougent toujours ensemble (w > 0,95) ;
// - 2 |Δ| : la pente du déplacement reste sous π/4 (π |Δ| / 2R), si bien que la limite ne se replie jamais sur
//   elle-même et qu'un décalage, même de 5 km, s'étale en une bosse de moins de 30° (corde Δ / R) : aucun virage
//   de plus de 90° de l'axe médian ;
// - voisin affiché (poignée majeure) : la poignée glissée tire la portion de limite jusqu'aux poignées voisines
//   visibles, qui restent en place ; les sommets mineurs qui les séparent suivent en douceur.
// Les sommets au-delà de R ne bougent pas du tout (mêmes coordonnées). Largeur gardée : chaque paire touchée
// glisse d'un bloc (même poids pour le sommet et son vis-à-vis) ; sommet seul : seule la limite saisie se
// déforme, la largeur et l'aimantation ne concernent que la paire saisie.

/** Rayon d'influence minimal (m) : les sommets quasi confondus de l'amorce bougent toujours ensemble. */
export const SOFT_MIN_RADIUS_M = 1_500;
/** Rayon d'au moins deux fois le déplacement : pente du déplacement sous π/4, bosse douce. */
export const SOFT_RADIUS_PER_MOVE = 2;

/** Abscisse curviligne (m) de chaque sommet d'une limite. */
function railStations(rail: readonly LonLat[]): number[] {
  const out = [0];
  for (let i = 1; i < rail.length; i++) out.push(out[i - 1]! + helperDistance(rail[i - 1]!, rail[i]!));
  return out;
}

/** Poids d'un sommet à la distance `d` (m, le long de la limite) du sommet saisi : cosinus surélevé, 1 en 0,
 *  0 à `radius` et au-delà. */
export function falloff(d: number, radius: number): number {
  if (!(radius > 0) || !(d < radius)) return d <= 0 ? 1 : 0;
  return 0.5 * (1 + Math.cos((Math.PI * Math.max(d, 0)) / radius));
}

/** Rayons d'influence (m) en arrière (vers le sommet 0) et en avant du sommet `index` d'une limite, pour un
 *  déplacement de `move` mètres (voir « Déformation douce »). */
export function influenceRadii(
  rail: readonly LonLat[],
  index: number,
  move: number,
  anchors?: Iterable<number> | null,
): { before: number; after: number } {
  const base = Math.max(SOFT_MIN_RADIUS_M, SOFT_RADIUS_PER_MOVE * Math.abs(move));
  const s = railStations(rail);
  let before = base;
  let after = base;
  if (anchors) {
    let previous = -1;
    let next = -1;
    for (const a of anchors) {
      if (!Number.isInteger(a) || a < 0 || a >= rail.length || a === index) continue;
      if (a < index && a > previous) previous = a;
      if (a > index && (next < 0 || a < next)) next = a;
    }
    if (previous >= 0) before = Math.max(before, s[index]! - s[previous]!);
    if (next >= 0) after = Math.max(after, s[next]! - s[index]!);
  }
  return { before, after };
}

/** Poids de chaque sommet de la limite pour un glissement de `move` mètres du sommet `index` (1 pour lui, 0
 *  au-delà du rayon d'influence). */
export function softWeights(
  rail: readonly LonLat[],
  index: number,
  move: number,
  anchors?: Iterable<number> | null,
): number[] {
  const s = railStations(rail);
  const { before, after } = influenceRadii(rail, index, move, anchors);
  return rail.map((_, j) => (j === index ? 1 : falloff(Math.abs(s[j]! - s[index]!), j < index ? before : after)));
}

/** Paire glissée d'un bloc : `lead` (sommet de la limite saisie) se déplace de (dx, dy) mètres dans son plan
 *  local, son vis-à-vis est reposé au même vecteur (m) du sommet déplacé, dans le plan local de la paire
 *  déplacée : la largeur se garde au centimètre (un report en degrés la ferait varier avec cos lat). */
function shiftPair(lead: LonLat, other: LonLat, dx: number, dy: number): [LonLat, LonLat] {
  const plane = planeOf([lead, other]);
  const [vx, vy] = toLocal(lead, plane);
  const [ox, oy] = toLocal(other, plane);
  return reposePair(fromLocal(vx + dx, vy + dy, plane), ox - vx, oy - vy);
}

/** Vis-à-vis reposé au vecteur (wx, wy) mètres de `lead` (deux passes pour le plan local de la paire). */
function reposePair(lead: LonLat, wx: number, wy: number): [LonLat, LonLat] {
  let other = fromLocal(wx, wy, planeOf([lead]));
  for (let k = 0; k < 2; k++) {
    const moved = planeOf([lead, other]);
    const [ax, ay] = toLocal(lead, moved);
    other = fromLocal(ax + wx, ay + wy, moved);
  }
  return [lead, other];
}

/** Point déplacé de (dx, dy) mètres dans son plan local. */
function shiftPoint(p: LonLat, dx: number, dy: number): LonLat {
  const plane = planeOf([p]);
  return fromLocal(dx, dy, plane);
}

/** Glissement d'un sommet, largeur gardée : la paire (le sommet et son vis-à-vis) suit `target`, position
 *  voulue du sommet saisi (pointeur moins l'écart de saisie, B28), et entraîne son voisinage en douceur (même
 *  poids pour chaque paire touchée : la largeur locale se garde). `start` est le fuseau au début du geste : la
 *  fonction s'applique à chaque image depuis lui, sans dérive. */
export function moveLinked(
  start: UserCorridor,
  side: Side,
  index: number,
  target: LonLat,
  options: MoveOptions = {},
): UserCorridor {
  const g = start.left[index];
  const d = start.right[index];
  if (!g || !d) return start;
  const plane = planeOf([g, d]);
  const [vx, vy] = toLocal(vertex(start, side, index), plane);
  let [tx, ty] = toLocal(target, plane);
  if (options.lateral) {
    const [nx, ny] = lateralDirection(start, index, plane);
    const along = (tx - vx) * nx + (ty - vy) * ny;
    [tx, ty] = [vx + along * nx, vy + along * ny];
  }
  const [ox, oy] = toLocal(side === "left" ? d : g, plane);
  const [grabbed, other] = reposePair(fromLocal(tx, ty, plane), ox - vx, oy - vy);
  let [newG, newD] = side === "left" ? [grabbed, other] : [other, grabbed];
  if (options.bounds) [newG, newD] = keepPairInside(newG, newD, options.bounds);
  // Déplacement effectif du sommet saisi (après la butée sur l'emprise), reporté sur le voisinage.
  const [mx, my] = toLocal(side === "left" ? newG : newD, plane);
  const [dx, dy] = [mx - vx, my - vy];
  const left = start.left.slice();
  const right = start.right.slice();
  left[index] = newG;
  right[index] = newD;
  const lead = side === "left" ? start.left : start.right;
  const weights = softWeights(lead, index, Math.hypot(dx, dy), options.anchors);
  weights.forEach((w, j) => {
    if (j === index || !(w > 0)) return;
    const [a, b] = side === "left" ? [start.left[j]!, start.right[j]!] : [start.right[j]!, start.left[j]!];
    let [movedLead, movedOther] = shiftPair(a, b, w * dx, w * dy);
    if (options.bounds) [movedLead, movedOther] = keepPairInside(movedLead, movedOther, options.bounds);
    left[j] = side === "left" ? movedLead : movedOther;
    right[j] = side === "left" ? movedOther : movedLead;
  });
  return { ...start, left, right };
}

/** Paire décalée d'un bloc pour rester dans l'emprise (chaque sommet borné s'il n'y tient pas). */
function keepPairInside(g: LonLat, d: LonLat, b: Bounds): [LonLat, LonLat] {
  const shift = (low: number, high: number, a: number, c: number) => {
    const min = Math.min(a, c);
    const max = Math.max(a, c);
    if (max - min > high - low) return 0;
    if (min < low) return low - min;
    if (max > high) return high - max;
    return 0;
  };
  const dLon = shift(b[0], b[2], g[0], d[0]);
  const dLat = shift(b[1], b[3], g[1], d[1]);
  return [clampToBounds([g[0] + dLon, g[1] + dLat], b), clampToBounds([d[0] + dLon, d[1] + dLat], b)];
}

export interface SnapResult {
  point: LonLat;
  /** Largeur de la paire (m) avec le point. */
  width: number;
  snapped: boolean;
}

/** Aimantation à la largeur par défaut : si la distance de `anchor` (vis-à-vis) à `target` est à moins de
 *  `ratio` de `doctrinal`, le point est reposé à exactement `doctrinal` mètres, sur la demi-droite
 *  anchor → target, ou sur la droite (`target`, `direction`) quand le mouvement est contraint (⇧). */
export function snapWidth(
  anchor: LonLat,
  target: LonLat,
  doctrinal: number,
  options: { ratio?: number; direction?: [number, number]; plane?: Plane } = {},
): SnapResult {
  const ratio = options.ratio ?? SNAP_RATIO;
  const width = helperDistance(anchor, target);
  if (!(doctrinal > 0) || !(Math.abs(width - doctrinal) <= ratio * doctrinal) || width === 0)
    return { point: target, width, snapped: false };
  const plane = options.plane ?? planeOf([anchor, target]);
  const [ax, ay] = toLocal(anchor, plane);
  const [px, py] = toLocal(target, plane);
  // Distance plane à viser : la haversine diffère du plan local d'un facteur quasi constant à cette échelle.
  const scale = Math.hypot(px - ax, py - ay) / width;
  const goal = doctrinal * scale;
  let x: number;
  let y: number;
  if (options.direction) {
    // Point de la droite p + λ·n à la distance `goal` de a, le plus proche de p.
    const [nx, ny] = options.direction;
    const wx = px - ax;
    const wy = py - ay;
    const b = wx * nx + wy * ny;
    const disc = b * b - (wx * wx + wy * wy - goal * goal);
    if (disc < 0) return { point: target, width, snapped: false };
    const r = Math.sqrt(disc);
    const lambda = Math.abs(-b + r) <= Math.abs(-b - r) ? -b + r : -b - r;
    [x, y] = [px + lambda * nx, py + lambda * ny];
  } else {
    const k = goal / Math.hypot(px - ax, py - ay);
    [x, y] = [ax + (px - ax) * k, ay + (py - ay) * k];
  }
  const point = fromLocal(x, y, plane);
  return { point, width: helperDistance(anchor, point), snapped: true };
}

export interface SingleMove {
  corridor: UserCorridor;
  /** Largeur de la paire après le mouvement (m). */
  width: number;
  /** Largeur par défaut de la clé de gabarit (médiane du mémento, m) : cible de l'aimantation. */
  doctrinal: number;
  /** Plage doctrinale [min, max] (m) de la clé de gabarit ; null pour un niveau sans fuseau doctrinal. */
  range: [number, number] | null;
  /** La largeur s'est calée sur la largeur par défaut. */
  snapped: boolean;
}

/** Glissement d'un seul sommet (⌥, ou « Garder la largeur » décochée), avec aimantation à la largeur par
 *  défaut de la clé de gabarit (`levelKey` ; union des familles du niveau sans clé), à moins de 3 % (`magnet`
 *  vrai par défaut). Les sommets voisins de la même limite suivent en douceur (« Déformation douce ») ; l'autre
 *  limite ne bouge pas. */
export function moveSingle(
  start: UserCorridor,
  side: Side,
  index: number,
  target: LonLat,
  options: MoveOptions & { magnet?: boolean; levelKey?: string | null } = {},
): SingleMove {
  const doctrine = forLevel(start.level, options.levelKey);
  const doctrinal = doctrine?.default_m ?? NaN;
  const range: [number, number] | null = doctrine ? [doctrine.min_m, doctrine.max_m] : null;
  const g = start.left[index];
  const d = start.right[index];
  if (!g || !d) return { corridor: start, width: widths(start)[index] ?? NaN, doctrinal, range, snapped: false };
  const anchor = side === "left" ? d : g;
  const origin = side === "left" ? g : d;
  const plane = planeOf([g, d]);
  let point = target;
  let direction: [number, number] | undefined;
  if (options.lateral) {
    direction = lateralDirection(start, index, plane);
    const [ox, oy] = toLocal(origin, plane);
    const [tx, ty] = toLocal(target, plane);
    const along = (tx - ox) * direction[0] + (ty - oy) * direction[1];
    point = fromLocal(ox + along * direction[0], oy + along * direction[1], plane);
  }
  point = clampToBounds(point, options.bounds);
  let snapped = false;
  if (options.magnet !== false) {
    const snap = snapWidth(anchor, point, doctrinal, { direction, plane });
    const inside = clampToBounds(snap.point, options.bounds);
    if (snap.snapped && inside[0] === snap.point[0] && inside[1] === snap.point[1]) {
      point = snap.point;
      snapped = true;
    }
  }
  // Déplacement effectif du sommet (aimantation et butée comprises), reporté en douceur sur sa limite.
  const [px, py] = toLocal(point, plane);
  const [qx, qy] = toLocal(origin, plane);
  const [dx, dy] = [px - qx, py - qy];
  const rail = (side === "left" ? start.left : start.right).slice();
  rail[index] = point;
  softWeights(side === "left" ? start.left : start.right, index, Math.hypot(dx, dy), options.anchors).forEach((w, j) => {
    if (j === index || !(w > 0)) return;
    rail[j] = clampToBounds(shiftPoint(rail[j]!, w * dx, w * dy), options.bounds);
  });
  const corridor = side === "left" ? { ...start, left: rail } : { ...start, right: rail };
  const width = snapped ? doctrinal : helperDistance(anchor, point);
  return { corridor, width, doctrinal, range, snapped };
}

/** Insertion d'une paire au milieu des deux segments en vis-à-vis entre les paires `index` et `index + 1`
 *  (poignée de milieu). L'interpolation bilinéaire du quadrilatère coupé en deux est inchangée : les LIMA et
 *  le repère restent en place. null au-delà de 64 paires. */
export function insertPair(c: UserCorridor, index: number): { corridor: UserCorridor; index: number } | null {
  if (c.left.length >= MAX_PAIRS || index < 0 || index + 1 >= c.left.length) return null;
  const mid = (a: LonLat, b: LonLat): LonLat => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const left = c.left.slice();
  const right = c.right.slice();
  left.splice(index + 1, 0, mid(c.left[index]!, c.left[index + 1]!));
  right.splice(index + 1, 0, mid(c.right[index]!, c.right[index + 1]!));
  return { corridor: { ...c, left, right }, index: index + 1 };
}

/** Retrait de la paire `index` (double-clic, Suppr) ; null s'il ne resterait qu'une paire. */
export function removePair(c: UserCorridor, index: number): UserCorridor | null {
  if (c.left.length <= MIN_PAIRS || index < 0 || index >= c.left.length) return null;
  return { ...c, left: c.left.filter((_, i) => i !== index), right: c.right.filter((_, i) => i !== index) };
}

// --- Plis de l'amorce (§2.3, déformation douce) ----------------------------------------------------------------
//
// L'amorce automatique (`seed_from_display` : segmentation sur le terrain, `pair_rails`) porte des plis : un petit
// segment de limite (16 m, 103 m sur Mourmelon, régiment, « S'emparer ») qui revient en arrière de l'axe. Les deux
// longs segments qui l'encadrent se chevauchent alors presque bord à bord : dès qu'un glissement les fait tourner
// l'un par rapport à l'autre, ils se croisent (« les limites se croisent »), même avec la déformation douce, qui
// fait tourner des segments qu'un glissement rigide laissait en place. Elle porte aussi des grappes de sommets
// quasi confondus (2 m, 16 m, 55 m sur « Prendre le contact ») où l'axe médian tourne court dès qu'on les glisse.
// Au premier geste sur un sommet, plis et sommets quasi confondus sont donc effacés : la paire est retirée (le
// sommet et son vis-à-vis ; la paire saisie est gardée de préférence), ou, si son vis-à-vis porte un vrai coude
// de l'autre limite, le sommet est reposé sur la corde de ses voisins. Changement de quelques dizaines de mètres,
// invisible à l'échelle d'un fuseau, envoyé avec le geste lui-même (une seule entrée d'historique) ; les LIMA
// gardent leur position sur la carte.
// Ce qui est effacé : un segment de moins de 250 m qui recule par rapport à l'axe (pli : aucune forme voulue n'en
// a) ; tout segment de moins de 250 m tant que le fuseau est l'amorce non retouchée (`seed`) ; ensuite, seulement
// les sommets à moins de 60 m l'un de l'autre (3 px au zoom courant), pour ne jamais retirer un sommet que
// l'utilisateur a posé à dessein.

/** Segment de limite au plus aussi court, s'il recule par rapport à l'axe (ou dans l'amorce non retouchée), effacé. */
export const FOLD_GAP_M = 250;
/** Sommets quasi confondus d'un fuseau déjà retouché : effacés en deçà de cet écart. */
export const DUPLICATE_GAP_M = 60;
/** Vis-à-vis à plus de cet écart de la corde de ses voisins : vrai coude, sa paire n'est pas retirée. */
const FACING_GAP_M = 50;

export interface FoldMerge {
  /** Fuseau sans pli (le même objet s'il n'en avait pas). */
  corridor: UserCorridor;
  /** Rang dans `corridor` de la paire de rang `i` avant l'effacement (paire retirée : celle qui la remplace). */
  index: (i: number) => number;
  /** Rangs (d'avant l'effacement) des paires retirées, croissants. */
  removed: number[];
}

/** Le segment k − 1 → k de la limite `rail` est-il à effacer : plus court que `near`, ou pli (moins de 250 m et
 *  à rebours de l'axe médian local) ? */
function foldAt(c: UserCorridor, rail: readonly LonLat[], k: number, near: number): boolean {
  const gap = helperDistance(rail[k - 1]!, rail[k]!);
  if (gap < near) return true;
  if (gap >= FOLD_GAP_M) return false;
  const n = c.left.length;
  const center = (i: number): LonLat => [(c.left[i]![0] + c.right[i]![0]) / 2, (c.left[i]![1] + c.right[i]![1]) / 2];
  const plane = planeOf([rail[k - 1]!, rail[k]!]);
  const [ax, ay] = toLocal(center(Math.max(k - 2, 0)), plane);
  const [bx, by] = toLocal(center(Math.min(k + 1, n - 1)), plane);
  const [px, py] = toLocal(rail[k - 1]!, plane);
  const [qx, qy] = toLocal(rail[k]!, plane);
  return (qx - px) * (bx - ax) + (qy - py) * (by - ay) < 0;
}

/** Écart (m) du point p au segment a → b. */
function chordGap(p: LonLat, a: LonLat, b: LonLat): number {
  const plane = planeOf([a, b, p]);
  const [ax, ay] = toLocal(a, plane);
  const [bx, by] = toLocal(b, plane);
  const [px, py] = toLocal(p, plane);
  const dx = bx - ax;
  const dy = by - ay;
  const length2 = dx * dx + dy * dy;
  const u = length2 > 0 ? clamp(((px - ax) * dx + (py - ay) * dy) / length2, 0, 1) : 0;
  return Math.hypot(px - ax - u * dx, py - ay - u * dy);
}

/** LIMA de `before` recalculées dans le repère de `after` à la même position sur la carte : extrémités projetées
 *  sur leur limite, courbures relocalisées, `default_s` décalée comme `s_ref` (le GDH calculé ne change pas). */
function carryLines(before: UserCorridor, after: UserCorridor): UserCorridor["lines"] {
  const from = frameOf(before);
  const to = frameOf(after);
  const lines: UserCorridor["lines"] = {};
  for (const name of presentLines(before)) {
    const line = before.lines[name]!;
    const ends: [number, number] = [
      clamp(to.railS(from.point(line.ends[0], -1), -1), 0, 1),
      clamp(to.railS(from.point(line.ends[1], 1), 1), 0, 1),
    ];
    const bends = line.bends.map(([s, t]): [number, number] => {
      const [s2, t2] = to.locate(from.point(s, t));
      return [clamp(s2, 0, 1), clamp(t2, -MAX_BEND_T, MAX_BEND_T)];
    });
    const shift = (ends[0] + ends[1]) / 2 - sRef(line);
    lines[name] = { ...line, ends, bends, default_s: clamp(line.default_s + shift, 0, 1) };
  }
  return lines;
}

/** Plis et sommets quasi confondus effacés (voir « Plis de l'amorce ») ; `keep` : rang de la paire saisie,
 *  gardée de préférence ; `seed` : le fuseau est l'amorce non retouchée (`corridor.custom` faux). */
export function mergeFolds(c: UserCorridor, options: { keep?: number | null; seed?: boolean } = {}): FoldMerge {
  const near = options.seed ? FOLD_GAP_M : DUPLICATE_GAP_M;
  let current = c;
  let alive = c.left.map((_, i) => i);
  const into = new Map<number, number>();
  const removed: number[] = [];
  let changed = false;
  for (let k = 1; k < current.left.length; k++) {
    const n = current.left.length;
    const foldLeft = foldAt(current, current.left, k, near);
    const foldRight = foldAt(current, current.right, k, near);
    if (!foldLeft && !foldRight) continue;
    // Paire à retirer : celle du bout du pli (k), ou celle d'avant au dernier rang ou si c'est la paire saisie.
    let drop = k === n - 1 ? k - 1 : k;
    const held = options.keep ?? -1;
    if (alive[drop] === held) {
      const other = drop === k ? k - 1 : k;
      if (other > 0 && other < n - 1) drop = other;
    }
    if (drop <= 0 || drop >= n - 1 || n <= MIN_PAIRS) continue;
    const partner = drop === k ? k - 1 : k;
    const folded = foldLeft && foldRight ? null : foldLeft ? current.left : current.right;
    const facing = folded === current.left ? current.right : current.left;
    if (!folded || chordGap(facing[drop]!, facing[drop - 1]!, facing[drop + 1]!) <= FACING_GAP_M) {
      into.set(alive[drop]!, alive[partner]!);
      removed.push(alive[drop]!);
      alive = alive.filter((_, i) => i !== drop);
      current = removePair(current, drop) ?? current;
      changed = true;
      k = Math.max(0, k - 2); // le segment recousu est réexaminé
      continue;
    }
    // Vis-à-vis sur un vrai coude de l'autre limite : le sommet effacé est reposé sur la corde de ses voisins, à la
    // fraction de l'autre limite (les paires restent en face l'une de l'autre).
    // Le sommet saisi ne saute pas sous le pointeur : c'est l'autre bout du segment qui est reposé.
    let r = k === n - 1 ? k - 1 : k;
    if (alive[r] === held) r = r === k ? k - 1 : k;
    if (r <= 0 || r >= n - 1) continue;
    const before = helperDistance(facing[r - 1]!, facing[r]!);
    const after = helperDistance(facing[r]!, facing[r + 1]!);
    const f = clamp(before + after > 0 ? before / (before + after) : 0.5, 0.1, 0.9);
    const a = folded[r - 1]!;
    const b = folded[r + 1]!;
    const rail = folded.slice();
    rail[r] = [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
    current = folded === current.left ? { ...current, left: rail } : { ...current, right: rail };
    changed = true;
  }
  if (!changed) return { corridor: c, index: (i) => i, removed: [] };
  const corridor = { ...current, lines: carryLines(c, current) };
  const position = new Map(alive.map((original, i) => [original, i]));
  const index = (i: number): number => {
    let at = i;
    for (let guard = 0; !position.has(at) && into.has(at) && guard < c.left.length; guard++) at = into.get(at)!;
    return position.get(at) ?? Math.min(Math.max(i, 0), alive.length - 1);
  };
  return { corridor, index, removed: removed.sort((x, y) => x - y) };
}

/** Géométrie de l'amorce (`corridor.seed`, ou toute paire de limites). */
export interface SeedRails {
  left: readonly LonLat[];
  right: readonly LonLat[];
}

/** Référence des paires modifiées : largeurs par paire (`seed_widths_m`, ou tenues à jour par
 *  `insertReference` / `removeReference`), ou géométrie de l'amorce. */
export type PairReference = readonly number[] | SeedRails | null | undefined;

export const isRails = (reference: PairReference): reference is SeedRails =>
  !!reference && !Array.isArray(reference) && "left" in reference && "right" in reference;

/** Largeur de référence de chaque paire depuis la géométrie de l'amorce : paire identique (sommets à moins
 *  de 1 m) → sa largeur ; sinon largeur de l'amorce à l'abscisse du milieu de la paire (repère de l'amorce).
 *  Jamais rang par rang : après une insertion et un retrait, les rangs ne correspondent plus. */
function seedReference(c: UserCorridor, seed: SeedRails): number[] {
  const seedWidths = widths(seed);
  const seedFrame = new CorridorFrame(seed.left, seed.right);
  const centers = frameOf(c).centers;
  return c.left.map((g, i) => {
    const d = c.right[i]!;
    const same = seed.left.findIndex((sg, k) => helperDistance(sg, g) < SAME_PAIR_M && helperDistance(seed.right[k]!, d) < SAME_PAIR_M);
    if (same >= 0) return seedWidths[same]!;
    const [s] = seedFrame.locate(centers[i]!);
    const at = clamp(s, 0, 1);
    return helperDistance(seedFrame.point(at, -1), seedFrame.point(at, 1));
  });
}

/** Largeur de référence de chaque paire, ou null sans référence (absente ou vide) :
 *  - géométrie de l'amorce : référence choisie paire par paire (`seedReference`) ;
 *  - largeurs alignées sur les paires courantes (même nombre) : rang par rang ;
 *  - largeurs non alignées (nombre de paires changé) : la largeur de la paire ramenée dans l'étendue des
 *    largeurs de référence (une paire est retouchée si elle sort de cette étendue élargie de 1 %). */
export function pairReferences(c: UserCorridor, reference?: PairReference): number[] | null {
  const found = widths(c);
  if (isRails(reference)) {
    if (reference.left.length >= 2 && reference.left.length === reference.right.length) return seedReference(c, reference);
    return null;
  }
  if (!Array.isArray(reference) || !reference.length) return null;
  if (reference.length === found.length) return reference.slice();
  const low = Math.min(...reference);
  const high = Math.max(...reference);
  return found.map((w) => clamp(w, low, high));
}

/** Paires dont la largeur s'écarte de plus de 1 % de celle de l'amorce (§2.2 ; `user_corridor.modified_pairs`,
 *  jeu de vecteurs `validate.json`, `modified`).
 *
 *  `reference` (voir `pairReferences`) : largeurs alignées — la forme à tenir pendant l'édition :
 *  `seed_widths_m` à l'entrée quand le modèle est l'amorce, puis `insertReference` / `removeReference` à chaque
 *  insertion ou retrait (un glissement lié reste non modifié) —, non alignées, ou géométrie de l'amorce ; absente
 *  ou vide (zone libre, `seed_widths_m` = []) : la largeur par défaut de la clé de gabarit `levelKey` (union des
 *  familles du niveau sans clé). */
export function modifiedPairs(c: UserCorridor, reference?: PairReference, levelKey?: string | null): number[] {
  const found = widths(c);
  const base = pairReferences(c, reference);
  const fallback = defaultWidth(c.level, levelKey);
  return found.flatMap((w, i) => (retouched(w, base ? base[i]! : fallback) ? [i] : []));
}

/** Largeurs de référence après `insertPair(before, index)` : la paire insérée (rang index + 1) reçoit sa
 *  largeur multipliée par le rapport moyen référence / largeur de ses deux voisines, si bien qu'une paire
 *  insérée entre deux paires non modifiées ne l'est pas. Référence non alignée sur `before` : rendue telle
 *  quelle. */
export function insertReference(reference: readonly number[], before: UserCorridor, index: number): number[] {
  const found = widths(before);
  const inserted = insertPair(before, index);
  if (!inserted || reference.length !== found.length) return reference.slice();
  const ratio = (k: number) => (found[k]! > 0 ? reference[k]! / found[k]! : 1);
  const width = widths(inserted.corridor)[inserted.index]!;
  const out = reference.slice();
  out.splice(inserted.index, 0, (width * (ratio(index) + ratio(index + 1))) / 2);
  return out;
}

/** Largeurs de référence après `removePair(c, index)`. */
export function removeReference(reference: readonly number[], index: number): number[] {
  return reference.filter((_, i) => i !== index);
}

// --- LIMA (§2.4) -------------------------------------------------------------------------------------------------

export interface LimaMove {
  corridor: UserCorridor;
  /** Décalage appliqué (fraction de l'axe). */
  delta: number;
  /** Voisine contre laquelle la LIMA a buté (avis existant, étiquette de la voisine qui clignote). */
  blockedBy: LineName | null;
}

/** Bornes d'un décalage (fraction de l'axe) : fuseau [0, 1] (bornes atteintes exactement) et voisines
 *  (bornes strictes, voisine nommée). */
interface Limits {
  lo: number;
  hi: number;
  loBy: LineName | null;
  hiBy: LineName | null;
}

/** Bornes de l'extrémité `side` de la LIMA `name` face à ses plus proches voisines présentes, règle de
 *  `mapdraw.order_violation` : une position est admise si elle dépasse la voisine de plus de 50 m, ou si elle
 *  éloigne de plus d'1 m une ligne déjà trop proche. Resserre `limits` (décalages en fraction de l'axe). */
function orderLimits(c: UserCorridor, name: LineName, side: 0 | 1, length: number, limits: Limits): void {
  const k = LINE_NAMES.indexOf(name);
  const current = c.lines[name]!.ends[side] * length;
  const before = LINE_NAMES.slice(0, k).reverse().find((n) => c.lines[n]);
  const after = LINE_NAMES.slice(k + 1).find((n) => c.lines[n]);
  if (before) {
    const lo = (Math.min(c.lines[before]!.ends[side] * length + LINE_ORDER_GAP_M, current + ESCAPE_M) - current) / length;
    if (lo >= limits.lo) [limits.lo, limits.loBy] = [lo, before];
  }
  if (after) {
    const hi = (Math.max(c.lines[after]!.ends[side] * length - LINE_ORDER_GAP_M, current - ESCAPE_M) - current) / length;
    if (hi <= limits.hi) [limits.hi, limits.hiBy] = [hi, after];
  }
}

/** Décalage demandé ramené dans les bornes : butée à 1 cm de la limite stricte d'une voisine (exactement sur
 *  le bord du fuseau) ; jamais au-delà du pointeur, ni à rebours : immobile si la butée l'exigerait. */
function limited(delta: number, limits: Limits, margin: number): [number, LineName | null] {
  const { lo, hi, loBy, hiBy } = limits;
  const below = loBy ? delta <= lo : delta < lo;
  const above = hiBy ? delta >= hi : delta > hi;
  if (!below && !above) return [delta, null];
  if (below) {
    const stop = loBy ? lo + margin : lo;
    return [stop <= 0 && (hiBy ? stop < hi : stop <= hi) ? stop : 0, loBy];
  }
  const stop = hiBy ? hi - margin : hi;
  return [stop >= 0 && (loBy ? stop > lo : stop >= lo) ? stop : 0, hiBy];
}

/** Glissement de la LIMA entière de `delta` (fraction de l'axe) : extrémités et courbures décalées du même Δs
 *  (inclinaison et courbures gardées), bornées au fuseau, butée sur les voisines (écart de 50 m sur chaque
 *  limite, comme `user_corridor.translate_line`). */
export function translateLimaBy(c: UserCorridor, name: LineName, delta: number): LimaMove {
  const line = c.lines[name];
  if (!line || !Number.isFinite(delta)) return { corridor: c, delta: 0, blockedBy: null };
  const length = frameOf(c).axisLength;
  const values = [...line.ends, ...line.bends.map(([s]) => s)];
  const limits: Limits = { lo: -Math.min(...values), hi: 1 - Math.max(...values), loBy: null, hiBy: null };
  if (length > 0) for (const side of [0, 1] as const) orderLimits(c, name, side, length, limits);
  const [applied, blockedBy] = limited(delta, limits, length > 0 ? STOP_MARGIN_M / length : 0);
  if (applied === 0) return { corridor: c, delta: 0, blockedBy };
  const moved: UserLine = {
    ...line,
    ends: [line.ends[0] + applied, line.ends[1] + applied],
    bends: line.bends.map(([s, t]) => [s + applied, t] as [number, number]),
    moved: true,
  };
  return { corridor: withLine(c, name, moved), delta: applied, blockedBy };
}

/** Glissement de la LIMA entière depuis le point de saisie (pas de saut au premier mouvement, B28), dans le repère
 *  K2 du fuseau au début du geste : Δs ≈ s(pointeur) − s(saisie), affiné pour que la LIMA reste sous le doigt
 *  (`followDelta`). */
export function translateLima(
  start: UserCorridor,
  name: LineName,
  grab: LonLat,
  pointer: LonLat,
  frame: CorridorFrame = frameOf(start),
): LimaMove {
  const wanted = frame.locate(pointer)[0] - frame.locate(grab)[0];
  return translateLimaBy(start, name, followDelta(start, name, grab, pointer, wanted, frame));
}

/** Δs à appliquer aux sommets de la LIMA pour qu'elle passe sous le pointeur. Le pointeur avance dans le repère de
 *  `wanted` depuis la saisie : un sommet (extrémité, courbure) saisi avance exactement d'autant, la LIMA reste sous
 *  le doigt. Un point pris au milieu d'un segment, tracé en ligne droite entre deux sommets décalés dans un repère
 *  courbe, n'avance d'autant qu'à peu près : sur une LIMA inclinée aux longs segments de l'amorce de Mourmelon,
 *  l'écart passait 500 m pour un geste de 2,6 km (revue du 04/10/2026). Le Δs qui annule l'écart du pointeur au
 *  segment saisi décalé (le côté du pointeur) est cherché entre des bornes autour de `wanted` (changement de signe,
 *  puis fausse position) ; repli sur `wanted` si rien ne l'améliore.
 *  LIMA saisie sur son prolongement, au-delà d'une extrémité (le tracé dessiné prolonge le segment d'extrémité,
 *  `linePoints`) : le segment d'extrémité est le segment saisi, prolongé. Hors des quadrilatères, `locate` replie
 *  la saisie et le pointeur sur des images lointaines (Keila : Δs de −39 km pour 20 m de geste, LIMA ramenée en
 *  butée à 1,3 km du doigt) ; l'amorce est alors le geste reporté sur l'extrémité dépassée, mesuré le long de sa
 *  limite (`railS`), et la recherche se fait autour d'elle. */
function followDelta(
  c: UserCorridor,
  name: LineName,
  grab: LonLat,
  pointer: LonLat,
  wanted: number,
  frame: CorridorFrame,
): number {
  const line = c.lines[name];
  if (!line) return wanted;
  const nodes: [number, number][] = [[line.ends[0], -1], ...line.bends, [line.ends[1], 1]];
  const vertices = nodes.map(([s, t]) => frame.point(s, t));
  // Segment saisi : celui du tracé le plus proche de la saisie (plan local autour de la saisie, en mètres).
  const kx = 111_320 * Math.cos((grab[1] * Math.PI) / 180);
  const local = (p: LonLat): [number, number] => [(p[0] - grab[0]) * kx, (p[1] - grab[1]) * 111_320];
  // Le premier et le dernier segment sont prolongés au-delà de leur extrémité (prolongement dessiné).
  const final = vertices.length - 2;
  let [index, fraction, nearest] = [0, 0, Infinity];
  for (let i = 0; i <= final; i++) {
    const [[ax, ay], [bx, by]] = [local(vertices[i]!), local(vertices[i + 1]!)];
    const [ex, ey] = [bx - ax, by - ay];
    const len2 = ex * ex + ey * ey;
    const f = len2 > 0 ? clamp((-ax * ex - ay * ey) / len2, i === 0 ? -Infinity : 0, i === final ? Infinity : 1) : 0;
    const d = Math.hypot(ax + f * ex, ay + f * ey);
    if (d < nearest) [index, fraction, nearest] = [i, f, d];
  }
  const beyond = (index === 0 && fraction < 0) || (index === final && fraction > 1);
  if (beyond) {
    // Amorce : le geste reporté sur l'extrémité dépassée (sur sa limite, t = −1 : gauche, t = 1 : droite), puis
    // l'avance de ce point le long de la limite. Le pointeur lui-même, loin dehors, se projette sur un autre tronçon
    // de la limite au moindre redan (Keila : −77 m pour un geste de −600 m).
    const [tipS, t] = fraction < 0 ? nodes[0]! : nodes[nodes.length - 1]!;
    const tip = frame.point(tipS, t);
    const k = Math.cos((tip[1] * Math.PI) / 180) / Math.cos((grab[1] * Math.PI) / 180);
    const moved: LonLat = [tip[0] + (pointer[0] - grab[0]) / (k || 1), tip[1] + pointer[1] - grab[1]];
    wanted = frame.railS(moved, t) - frame.railS(tip, t);
  } else if (fraction <= 0 || fraction >= 1 || !(Math.abs(wanted) > 0)) return wanted;
  const [[s0, t0], [s1, t1]] = [nodes[index]!, nodes[index + 1]!];
  const [px, py] = local(pointer);
  const segment = (delta: number) => [local(frame.point(s0 + delta, t0)), local(frame.point(s1 + delta, t1))] as const;
  // Écart signé (m) du pointeur à la droite du segment saisi décalé de Δs, moins celui de la saisie (la saisie peut
  // tomber à côté du tracé, dans la zone de préhension) : nul quand la LIMA est sous le doigt comme à la saisie.
  const offset = (delta: number, x: number, y: number) => {
    const [[ax, ay], [bx, by]] = segment(delta);
    const len = Math.hypot(bx - ax, by - ay);
    return len > 0 ? ((x - ax) * (by - ay) - (y - ay) * (bx - ax)) / len : 0;
  };
  const base = offset(0, 0, 0);
  const miss = (delta: number) => offset(delta, px, py) - base;
  const first = Math.abs(miss(wanted));
  if (!(first > 0.01)) return wanted;
  if (beyond) {
    // Saisie sur le prolongement : Δs qui ramène sous le doigt le point saisi de la LIMA prolongée (même distance
    // au-delà de l'extrémité), au sens des moindres carrés, l'écart au tracé pesant dix fois plus que le glissement
    // du point le long du tracé. La droite du segment d'extrémité repasse sous le pointeur pour d'autres Δs, et ne
    // s'en écarte presque pas sur un éventail de l'amorce (extrémité au pivot) : l'écart seul ne départage pas.
    // Recherche sur 32 pas autour de l'amorce (deux fois son ampleur, 2 km au moins : l'extrémité d'une LIMA posée
    // au pivot d'un éventail ne bouge presque pas, et le geste reporté sur elle non plus), bornée au fuseau, puis
    // section dorée autour du meilleur pas.
    const outward = (delta: number, x: number, y: number) => {
      const [[ax, ay], [bx, by]] = segment(delta);
      const [tx, ty, ox, oy] = fraction < 0 ? [ax, ay, bx, by] : [bx, by, ax, ay];
      const len = Math.hypot(tx - ox, ty - oy);
      return len > 0 ? ((x - tx) * (tx - ox) + (y - ty) * (ty - oy)) / len : 0;
    };
    const held = outward(0, 0, 0);
    const error = (delta: number) => Math.hypot(miss(delta), 0.1 * (outward(delta, px, py) - held));
    const span = Math.max(2 * Math.abs(wanted), frame.axisLength > 0 ? 2000 / frame.axisLength : 0);
    const values = nodes.map(([s]) => s);
    const [from, to] = [Math.max(wanted - span, -Math.min(...values)), Math.min(wanted + span, 1 - Math.max(...values))];
    if (!(to > from)) return wanted;
    const STEPS = 32;
    const at = (i: number) => from + ((to - from) * i) / STEPS;
    const errors = Array.from({ length: STEPS + 1 }, (_, i) => error(at(i)));
    const ratio = (Math.sqrt(5) - 1) / 2;
    const minima: [number, number][] = [];
    for (let i = 0; i <= STEPS; i++) {
      if ((i > 0 && errors[i - 1]! < errors[i]!) || (i < STEPS && errors[i + 1]! < errors[i]!)) continue;
      let [lo, hi] = [at(Math.max(i - 1, 0)), at(Math.min(i + 1, STEPS))];
      for (let j = 0; j < 40; j++) {
        const [x1, x2] = [hi - ratio * (hi - lo), lo + ratio * (hi - lo)];
        if (error(x1) <= error(x2)) hi = x2;
        else lo = x1;
      }
      const refined = (lo + hi) / 2;
      const e = error(refined);
      minima.push(e <= errors[i]! ? [refined, e] : [at(i), errors[i]!]);
    }
    // Minima à 1 m près du meilleur (éventail : tous passent sous le doigt à moins d'1 m) : le plus proche de
    // l'amorce, pour que la LIMA ne saute pas d'un bout à l'autre de l'éventail sous un doigt presque immobile.
    const bestE = Math.min(...minima.map(([, e]) => e));
    let chosen = wanted;
    let gapToWanted = Infinity;
    for (const [delta, e] of minima)
      if (e <= bestE + 1 && Math.abs(delta - wanted) < gapToWanted) [chosen, gapToWanted] = [delta, Math.abs(delta - wanted)];
    return chosen;
  }
  // Garde-fou : Δs du signe du geste et au plus double de la mesure du repère (segment presque parallèle à l'axe,
  // autre croisement du segment prolongé).
  const [low, high] = [Math.min(0, 2 * wanted), Math.max(0, 2 * wanted)];
  // Changements de signe de l'écart sur 16 pas entre les bornes, chacun affiné par fausse position (Illinois) ; le
  // Δs retenu est le plus proche de l'amorce. Sans changement de signe : le pas qui réduit le plus l'écart.
  const STEPS = 16;
  let [found, fallback, fallbackGap] = [NaN, wanted, first];
  let [da, ha] = [low, miss(low)];
  for (let i = 1; i <= STEPS; i++) {
    const db = low + ((high - low) * i) / STEPS;
    const hb = miss(db);
    if (Math.abs(hb) < fallbackGap) [fallback, fallbackGap] = [db, Math.abs(hb)];
    if (ha * hb <= 0 && ha !== hb) {
      let [a, fa, b, fb, kept] = [da, ha, db, hb, 0];
      for (let j = 0; j < 30 && Math.abs(b - a) > 1e-12; j++) {
        const c = b - (fb * (b - a)) / (fb - fa);
        const fc = miss(c);
        if (Math.abs(fc) < 0.01) [a, fa, b, fb] = [c, fc, c, fc];
        else if (fc * fb < 0) {
          [a, fa] = [b, fb];
          kept = 0;
        } else {
          if (kept === 1) fa /= 2;
          kept = 1;
        }
        [b, fb] = [c, fc];
      }
      const root = Math.abs(fa) < Math.abs(fb) ? a : b;
      if (Number.isFinite(root) && (Number.isNaN(found) || Math.abs(root - wanted) < Math.abs(found - wanted))) found = root;
    }
    [da, ha] = [db, hb];
  }
  return Number.isNaN(found) ? fallback : found;
}

/** Extrémité d'une LIMA glissée le long de sa limite (projection du pointeur) : la ligne s'incline. Butée sur
 *  les voisines de cette limite (écart de 50 m). */
export function moveLimaEnd(
  start: UserCorridor,
  name: LineName,
  side: Side,
  pointer: LonLat,
  frame: CorridorFrame = frameOf(start),
): LimaMove {
  const line = start.lines[name];
  if (!line) return { corridor: start, delta: 0, blockedBy: null };
  const k = side === "left" ? 0 : 1;
  const target = clamp(frame.railS(pointer, side === "left" ? -1 : 1), 0, 1);
  const length = frame.axisLength;
  const limits: Limits = { lo: -line.ends[k], hi: 1 - line.ends[k], loBy: null, hiBy: null };
  if (length > 0) orderLimits(start, name, k, length, limits);
  const [delta, blockedBy] = limited(target - line.ends[k], limits, length > 0 ? STOP_MARGIN_M / length : 0);
  if (delta === 0) return { corridor: start, delta: 0, blockedBy };
  const ends: [number, number] = [...line.ends];
  ends[k] = clamp(line.ends[k] + delta, 0, 1);
  return { corridor: withLine(start, name, { ...line, ends, moved: true }), delta, blockedBy };
}

/** Courbure d'une LIMA déplacée librement, ramenée à l'intérieur du fuseau (s ∈ [0, 1], |t| ≤ 0,98). Toute
 *  retouche du tracé d'une LIMA (courbure glissée, ajoutée ou retirée) la marque glissée (`moved`) : les
 *  courbures de l'amorce, calées sur le terrain, ne le sont pas. */
export function moveBend(
  start: UserCorridor,
  name: LineName,
  index: number,
  pointer: LonLat,
  frame: CorridorFrame = frameOf(start),
): UserCorridor {
  const line = start.lines[name];
  if (!line || !line.bends[index]) return start;
  const [s, t] = frame.locate(pointer);
  const bends = line.bends.slice();
  bends[index] = [clamp(s, 0, 1), clamp(t, -MAX_BEND_T, MAX_BEND_T)];
  return withLine(start, name, { ...line, bends, moved: true });
}

/** Courbure ajoutée au rang `index` (poignée de milieu entre deux sommets de la LIMA) ; null au-delà de 14. */
export function insertBend(
  c: UserCorridor,
  name: LineName,
  index: number,
  point: LonLat,
  frame: CorridorFrame = frameOf(c),
): UserCorridor | null {
  const line = c.lines[name];
  if (!line || line.bends.length >= MAX_BENDS || index < 0 || index > line.bends.length) return null;
  const [s, t] = frame.locate(point);
  const bends = line.bends.slice();
  bends.splice(index, 0, [clamp(s, 0, 1), clamp(t, -MAX_BEND_T, MAX_BEND_T)]);
  return withLine(c, name, { ...line, bends, moved: true });
}

/** Courbure retirée (⌫ / Suppr). */
export function removeBend(c: UserCorridor, name: LineName, index: number): UserCorridor {
  const line = c.lines[name];
  if (!line || !line.bends[index]) return c;
  return withLine(c, name, { ...line, bends: line.bends.filter((_, i) => i !== index), moved: true });
}

/** Ajout d'une LIMA manquante : à sa fraction par défaut pour la mission (`defaultS`, quand le serveur la
 *  donne) si l'ordre le permet, sinon au milieu entre ses voisines présentes (`user_corridor._fit`). Barre
 *  perpendiculaire, GDH calculé. null si la ligne existe déjà. */
export function addLine(c: UserCorridor, name: LineName, defaultS?: number | null): UserCorridor | null {
  if (c.lines[name] || !LINE_NAMES.includes(name)) return null;
  const length = frameOf(c).axisLength;
  const gap = length > 0 ? LINE_ORDER_GAP_M / length : 0;
  const k = LINE_NAMES.indexOf(name);
  const before = LINE_NAMES.slice(0, k).flatMap((n) => (c.lines[n] ? [c.lines[n]!] : []));
  const after = LINE_NAMES.slice(k + 1).flatMap((n) => (c.lines[n] ? [c.lines[n]!] : []));
  const low = Math.max(0, ...before.map((line) => Math.max(...line.ends)));
  const high = Math.min(1, ...after.map((line) => Math.min(...line.ends)));
  const wanted = defaultS ?? NaN;
  const s = low + gap <= wanted && wanted <= high - gap ? wanted : (low + high) / 2;
  return withLine(c, name, { ends: [s, s], bends: [], default_s: s, hours: null, moved: false });
}

/** LIMA retirable : la présente de plus haut rang, jamais L0 (« Retirez d'abord L3 »). */
export function removableLine(c: UserCorridor): LineName | null {
  const present = presentLines(c);
  const last = present[present.length - 1];
  return last && last !== "L0" ? last : null;
}

/** Retrait de la LIMA présente de plus haut rang ; null s'il ne reste que L0. */
export function removeLastLine(c: UserCorridor): UserCorridor | null {
  const name = removableLine(c);
  return name ? withLine(c, name, null) : null;
}

/** Retour d'une LIMA à `default_s` : barre perpendiculaire, sans GDH saisi ni courbure. */
export function resetLine(c: UserCorridor, name: LineName): UserCorridor {
  const line = c.lines[name];
  if (!line) return c;
  return withLine(c, name, { ...line, ends: [line.default_s, line.default_s], bends: [], hours: null, moved: false });
}

/** Toutes les LIMA ramenées à `default_s`, sans GDH saisi ni courbure. */
export function resetLines(c: UserCorridor): UserCorridor {
  return presentLines(c).reduce(resetLine, c);
}

/** GDH saisi (heures après le début) ou null pour revenir au GDH calculé. */
export function setLineHours(c: UserCorridor, name: LineName, hours: number | null): UserCorridor {
  const line = c.lines[name];
  if (!line) return c;
  return withLine(c, name, { ...line, hours });
}

/** Polyligne d'une LIMA entre les deux limites (sans prolongement). */
export function lineVertices(c: UserCorridor, name: LineName, frame: CorridorFrame = frameOf(c)): LonLat[] {
  const line = c.lines[name];
  if (!line) return [];
  return verticesOf(frame, line);
}

function verticesOf(frame: CorridorFrame, line: UserLine): LonLat[] {
  return [frame.point(line.ends[0], -1), ...line.bends.map(([s, t]) => frame.point(s, t)), frame.point(line.ends[1], 1)];
}

/** Polyligne affichée de la LIMA (`user_corridor.line_points`) : prolongée de 500 m au-delà de chaque limite,
 *  dans le prolongement du segment d'extrémité. */
export function linePoints(c: UserCorridor, name: LineName, frame: CorridorFrame = frameOf(c)): LonLat[] {
  const points = lineVertices(c, name, frame);
  if (points.length < 2) return points;
  const first = moveM(points[0]!, bearingDeg(points[1]!, points[0]!), MARGIN_M);
  const end = points[points.length - 1]!;
  const last = moveM(end, bearingDeg(points[points.length - 2]!, end), MARGIN_M);
  return [first, ...points, last];
}

// --- GDH (§5.4) -----------------------------------------------------------------------------------------------------

/** (nom, GDH avant la passe croissante, saisi) pour L0 à L3 (`user_corridor._raw_hours`). */
function rawHours(c: { lines: Partial<Record<string, UserLine>>; level?: string | null }): [LineName, number, boolean][] {
  const out: [LineName, number, boolean][] = [];
  let previous: number | null = null;
  const bases = lineBases(c.level);
  for (const name of LINE_NAMES) {
    const base = bases[name];
    const line = c.lines[name];
    let hours: number;
    if (!line) {
      hours = previous === null ? base : Math.max(base, previous + 1);
      out.push([name, hours, false]);
    } else if (line.hours !== null) {
      hours = Math.trunc(line.hours);
      out.push([name, hours, true]);
    } else {
      hours = base + roundHours((sRef(line) - line.default_s) * HOURS_PER_AXIS);
      out.push([name, hours, false]);
    }
    previous = hours;
  }
  return out;
}

/** GDH effectifs des quatre LIMA, en heures après le début (`user_corridor.effective_hours`) : ligne présente
 *  = heure du niveau (`lineBases`) plus son décalage (ou le GDH saisi) ; ligne absente = max(base, précédente + 1) ; puis passe
 *  strictement croissante. Aperçu des `LineTag` pendant les gestes. */
export function previewHours(c: { lines: Partial<Record<string, UserLine>>; level?: string | null }): Record<LineName, number> {
  const out = {} as Record<LineName, number>;
  let previous: number | null = null;
  for (const [name, raw] of rawHours(c)) {
    const hours: number = previous === null ? raw : Math.max(raw, previous + 1);
    out[name] = hours;
    previous = hours;
  }
  return out;
}

/** Décalages des quatre LIMA (heures) par rapport aux heures du niveau (`offsets_all`, `lineBases`). */
export function offsetsAll(c: { lines: Partial<Record<string, UserLine>>; level?: string | null }): Record<LineName, number> {
  const hours = previewHours(c);
  const bases = lineBases(c.level);
  return Object.fromEntries(LINE_NAMES.map((n) => [n, hours[n] - bases[n]])) as Record<LineName, number>;
}

/** Première LIMA dont le GDH saisi ne suit pas strictement le GDH effectif précédent (refusé, jamais
 *  corrigé), ou null. */
function hoursUnordered(c: { lines: Partial<Record<string, UserLine>>; level?: string | null }): LineName | null {
  let previous: number | null = null;
  for (const [name, hours, entered] of rawHours(c)) {
    if (entered && previous !== null && hours <= previous) return name;
    previous = previous === null ? hours : Math.max(hours, previous + 1);
  }
  return null;
}

// --- Validation (user_corridor.validate) ---------------------------------------------------------------------------

function structureErrors(c: ParsedCorridor, level: string): RuleCode[] {
  const found: RuleCode[] = [];
  if (c.v !== VERSION) found.push("version");
  if (!(LEVELS as readonly string[]).includes(c.level) || c.level !== level) found.push("level");
  const pairs = (n: number) => MIN_PAIRS <= n && n <= MAX_PAIRS;
  if (!(pairs(c.left.length) && pairs(c.right.length))) found.push("pairs_count");
  if (c.left.length !== c.right.length) found.push("pairs_mismatch");
  if (![...c.left, ...c.right].every((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]))) found.push("finite");
  const names = Object.keys(c.lines);
  if (!(names.length >= 1 && names.length <= MAX_LINES)) found.push("lines_count");
  if (names.some((n) => !(LINE_NAMES as readonly string[]).includes(n))) found.push("line_name");
  if (names.length && !("L0" in c.lines)) found.push("l0_missing");
  const lines = Object.values(c.lines);
  const fraction = (x: number) => Number.isFinite(x) && x >= 0 && x <= 1;
  if (!lines.every((line) => line.ends.every(fraction))) found.push("ends_range");
  if (lines.some((line) => line.bends.length > MAX_BENDS)) found.push("bends_count");
  if (!lines.every((line) => line.bends.every(([s, t]) => fraction(s) && Number.isFinite(t) && Math.abs(t) <= MAX_BEND_T)))
    found.push("bend_range");
  if (!lines.every((line) => fraction(line.default_s))) found.push("default_s_range");
  const hours = (h: number | null) => h === null || (Number.isInteger(h) && MIN_HOURS <= h && h <= MAX_HOURS);
  if (!lines.every((line) => hours(line.hours))) found.push("hours_range");
  return found;
}

const insideBox = (box: Bounds, p: LonLat) => box[0] <= p[0] && p[0] <= box[2] && box[1] <= p[1] && p[1] <= box[3];

function placeErrors(c: ParsedCorridor, bounds: Bounds | null): RuleCode[] {
  const found: RuleCode[] = [];
  const points = [...c.left, ...c.right];
  if (!points.every((p) => insideBox(WORLD, p))) found.push("world");
  const jumps = (rail: LonLat[]) => rail.some((a, i) => i + 1 < rail.length && Math.abs(rail[i + 1]![0] - a[0]) > 180);
  if (jumps(c.left) || jumps(c.right)) found.push("antimeridian");
  if (bounds && !points.every((p) => insideBox(bounds, p))) found.push("bounds");
  return found;
}

/** Contexte de l'alerte de largeur (docs/15 §2.4) : clé de gabarit (`deployment.level_key` ; sans clé du
 *  niveau, aucune alerte) et référence des paires. Pendant l'édition, le modèle n'a pas son amorce : la référence
 *  est l'amorce que compare le serveur (`reference_seed`, `widthContextOf` ; vide sans amorce : toutes les
 *  paires) et `legacy` le fuseau antérieur (`width_basis` « legacy » : toutes les paires). Sans `reference`,
 *  l'amorce que porte le fuseau (forme stockée, `seed` et `seed_basis` « web ») sert de référence, comme au
 *  serveur. */
export interface WidthContext {
  levelKey?: string | null;
  reference?: PairReference;
  legacy?: boolean;
}

/** Contexte de l'alerte de largeur d'un fuseau reçu du serveur (docs/15 §12.3) : géométrie de l'amorce de
 *  référence (`reference_seed`, la règle exacte du serveur, `seedReference`, quel que soit le nombre de paires),
 *  vide sans amorce (fuseau antérieur, zone libre, sans fuseau : toutes les paires). */
export function widthContextOf(
  corridor: Pick<Corridor, "reference_seed" | "width_basis"> | null | undefined,
  levelKey: string | null | undefined,
): WidthContext {
  return {
    levelKey,
    reference: corridor?.reference_seed ?? [],
    legacy: corridor?.width_basis === "legacy",
  };
}

/** Largeurs, doctrine et paires en alerte d'un fuseau (règle du §2.4, `user_corridor._warnings`) ; null sans
 *  clé de gabarit du niveau (aucune alerte) ou pour des limites inégales. */
export function widthAlerts(
  c: UserCorridor | ParsedCorridor,
  level: string,
  context: WidthContext = {},
): { widths: number[]; doctrine: WidthDoctrine; alerts: number[]; worst: number | null } | null {
  // Sans clé de gabarit du niveau (absente ou d'un autre niveau, docs/15 §2.1) : aucune alerte, à tous les niveaux.
  if (!keyed(level, context.levelKey) || c.left.length !== c.right.length || c.left.length < 2) return null;
  const doctrine = forLevel(level, context.levelKey)!;
  const parsed = c as Partial<ParsedCorridor>;
  let references: number[] | null = null;
  if (!context.legacy) {
    if (context.reference !== undefined) references = pairReferences(c as UserCorridor, context.reference);
    else if (c.seed && parsed.seed_basis === "web") references = pairReferences(c as UserCorridor, c.seed);
  }
  const found = widths(c);
  const alerts = alertPairs(found, doctrine, references);
  return { widths: found, doctrine, alerts, worst: worstWidth(found, alerts, doctrine) };
}

/** Alerte de largeur (pire paire), au texte exact du serveur. */
function widthWarnings(c: ParsedCorridor, level: string, context: WidthContext): string[] {
  const found = widthAlerts(c, level, context);
  return found && found.worst !== null ? [widthWarning(found.doctrine, found.worst)] : [];
}

/** Plan local du fuseau (centré sur le barycentre de ses sommets), en mètres. */
function xyPlane(c: { left: LonLat[]; right: LonLat[] }): (p: LonLat) => XY {
  const plane = planeOf([...c.left, ...c.right]);
  return (p) => toLocal(p, plane);
}

function meanWidth(c: { left: LonLat[]; right: LonLat[] }): number {
  const found = widths(c);
  return found.length ? found.reduce((a, b) => a + b, 0) / found.length : 0;
}

/** Échelle des tolérances géométriques : W' = max(largeur moyenne ; 18 150 m) (`user_corridor._scale_width`). */
function scaleWidth(c: { left: LonLat[]; right: LonLat[] }): number {
  return Math.max(meanWidth(c), GEOMETRY_SCALE_FLOOR_M);
}

/** Direction de l'axe médian à chaque paire, de `half` mètres en arrière à `half` mètres en avant. */
function tangents(centers: XY[], half: number): XY[] {
  const cumulative = [0];
  for (let i = 0; i + 1 < centers.length; i++)
    cumulative.push(cumulative[i]! + Math.hypot(centers[i + 1]![0] - centers[i]![0], centers[i + 1]![1] - centers[i]![1]));
  const total = cumulative[cumulative.length - 1]!;
  const at = (arc: number): XY => {
    arc = clamp(arc, 0, total);
    let k = 0;
    while (k + 1 < cumulative.length && cumulative[k + 1]! <= arc) k++; // bisect_right − 1
    k = clamp(k, 0, centers.length - 2);
    const span = cumulative[k + 1]! - cumulative[k]!;
    const f = span > 0 ? (arc - cumulative[k]!) / span : 0;
    const a = centers[k]!;
    const b = centers[k + 1]!;
    return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
  };
  return cumulative.map((arc) => {
    const a = at(arc - half);
    const b = at(arc + half);
    return [b[0] - a[0], b[1] - a[1]];
  });
}

/** Orientation de chaque paire : produit vectoriel (direction médiane, Gᵢ − Cᵢ) ≤ 0. */
function pairInverted(c: { left: LonLat[]; right: LonLat[] }): boolean[] {
  const xy = xyPlane(c);
  const centers = c.left.map((g, i) => xy([(g[0] + c.right[i]![0]) / 2, (g[1] + c.right[i]![1]) / 2]));
  return tangents(centers, ORIENT_HALF_RATIO * scaleWidth(c)).map(([dx, dy], i) => {
    const [gx, gy] = xy(c.left[i]!);
    return dx * (gy - centers[i]![1]) - dy * (gx - centers[i]![0]) <= 0;
  });
}

/** Limites gauche et droite permutées (LIMA et amorce comprises). */
function mirror<T extends ParsedCorridor | UserCorridor>(c: T): T {
  const lines = Object.fromEntries(
    Object.entries(c.lines as Record<string, UserLine>).map(([n, line]) => [
      n,
      { ...line, ends: [line.ends[1], line.ends[0]], bends: line.bends.map(([s, t]) => [s, -t]) } as UserLine,
    ]),
  );
  return {
    ...c,
    left: c.right,
    right: c.left,
    seed: c.seed ? { left: c.seed.right, right: c.seed.left } : c.seed,
    lines,
  };
}

/** Permutation automatique quand toutes les paires sont inversées (§3.1) ; sinon le fuseau tel quel. */
export function orient<T extends ParsedCorridor | UserCorridor>(c: T): T {
  if (c.left.length !== c.right.length || c.left.length < 2) return c;
  return pairInverted(c).every(Boolean) ? mirror(c) : c;
}

/** Premier virage de plus de 90° de l'axe médian simplifié : indice du sommet (paire) où il a lieu. */
function sharpTurn(centers: XY[], tolerance: number): number | null {
  const kept = douglasPeucker(centers, tolerance);
  for (let k = 1; k + 1 < kept.length; k++) {
    const p = centers[kept[k - 1]!]!;
    const q = centers[kept[k]!]!;
    const r = centers[kept[k + 1]!]!;
    const a = [q[0] - p[0], q[1] - p[1]] as const;
    const b = [r[0] - q[0], r[1] - q[1]] as const;
    const angle = (Math.abs(Math.atan2(a[0] * b[1] - a[1] * b[0], a[0] * b[0] + a[1] * b[1])) * 180) / Math.PI;
    if (angle > MAX_TURN_DEG) return kept[k]!;
  }
  return null;
}

type Fault<R> = { rule: R; pairs: number[]; lines: LineName[] } | null;

function railError(c: ParsedCorridor, level: string, levelKey: string | null | undefined): Fault<RuleCode> {
  const f = new CorridorFrame(c.left, c.right);
  const n = c.left.length;
  // Longueur d'axe : tout le fuseau est en cause.
  if (!((MIN_AXIS_BY_LEVEL[level] ?? MIN_AXIS_M) <= f.axisLength && f.axisLength <= (MAX_AXIS_BY_LEVEL[level] ?? MAX_AXIS_M)))
    return { rule: "axis_length", pairs: c.left.map((_, i) => i), lines: [] };
  for (let i = 0; i + 1 < n; i++)
    if (helperDistance(f.centers[i]!, f.centers[i + 1]!) < MIN_CENTER_STEP_M)
      return { rule: "degenerate", pairs: [i, i + 1], lines: [] };
  const inverted = pairInverted(c);
  if (inverted.some(Boolean)) return { rule: "orientation", pairs: inverted.flatMap((x, i) => (x ? [i] : [])), lines: [] };
  // Prédicats de shapely en degrés (lon, lat), comme le serveur.
  for (const rail of [c.left, c.right]) {
    const self = selfFault(rail);
    if (self) return { rule: "crossing", pairs: [...new Set(self.flat())].sort((a, b) => a - b), lines: [] };
  }
  const hit = polylinesIntersect(c.left, c.right);
  if (hit) return { rule: "crossing", pairs: [...new Set([hit[0], hit[0] + 1, hit[1], hit[1] + 1])], lines: [] };
  const ring = ringFault([...c.left, ...c.right.slice().reverse()]);
  if (ring) {
    // Segment k du contour gauche + droite inversée → paires concernées.
    const pairsOf = (k: number) =>
      k < n - 1 ? [k, k + 1] : k === n - 1 ? [n - 1] : k < 2 * n - 1 ? [2 * n - 2 - k, 2 * n - 1 - k] : [0];
    return { rule: "polygon", pairs: [...new Set([...pairsOf(ring[0]), ...pairsOf(ring[1])])], lines: [] };
  }
  const xy = xyPlane(c);
  const turn = sharpTurn(f.centers.map(xy), TURN_SIMPLIFY_RATIO * scaleWidth(c));
  if (turn !== null) return { rule: "turn", pairs: [turn], lines: [] };
  // Bornes dures de la clé de gabarit (union des familles du niveau sans clé), docs/15 §2.4.
  const doctrine = forLevel(level, levelKey);
  const bad = doctrine ? widths(c).flatMap((w, i) => (withinHardBounds(w, doctrine) ? [] : [i])) : [];
  if (bad.length) return { rule: "width", pairs: bad, lines: [] };
  return null;
}

function lineError(c: ParsedCorridor): Fault<RuleCode> {
  const f = new CorridorFrame(c.left, c.right);
  const names = presentLines(c);
  const gap = f.axisLength > 0 ? LINE_ORDER_GAP_M / f.axisLength : 0;
  for (const side of [0, 1] as const) {
    const positions = names.map((n) => c.lines[n]!.ends[side]);
    for (let i = 0; i + 1 < positions.length; i++)
      if (positions[i + 1]! - positions[i]! < gap - 1e-12)
        return { rule: "line_order", pairs: [], lines: [names[i]!, names[i + 1]!] };
  }
  const shapes = new Map(names.map((n) => [n, verticesOf(f, c.lines[n]!)]));
  for (let i = 0; i < names.length; i++)
    for (const b of names.slice(i + 1))
      if (polylinesIntersect(shapes.get(names[i]!)!, shapes.get(b)!))
        return { rule: "line_crossing", pairs: [], lines: [names[i]!, b] };
  // Dans le fuseau : chaque point contrôlé de chaque segment est dans le contour ou à moins de 0,02 W.
  const xy = xyPlane(c);
  const polygon = [...c.left, ...c.right.slice().reverse()].map(xy);
  const tolerance = LINE_INSIDE_RATIO * scaleWidth(c);
  for (const n of names) {
    const vertices = shapes.get(n)!.map(xy);
    for (let j = 0; j + 1 < vertices.length; j++) {
      const [ax, ay] = vertices[j]!;
      const [bx, by] = vertices[j + 1]!;
      for (let k = 0; k <= LINE_INSIDE_SAMPLES; k++) {
        const u = k / LINE_INSIDE_SAMPLES;
        if (polygonDistance(polygon, [ax + (bx - ax) * u, ay + (by - ay) * u]) > tolerance)
          return { rule: "line_inside", pairs: [], lines: [n] };
      }
    }
  }
  const unordered = hoursUnordered(c);
  if (unordered) return { rule: "hours_order", pairs: [], lines: [unordered] };
  return null;
}

/** Règles du §3.1, exactement comme `user_corridor.validate_rules` : structure (toutes les fautes), emprise
 *  (toutes), limites (première faute), LIMA (première faute) ; limites entièrement inversées admises
 *  (permutation automatique). `corridor` : modèle stocké, ou toute valeur reçue (lecture tolérante :
 *  « format » si elle est illisible) ; `bounds` : emprise admise ou null ; `level` : niveau du dossier ;
 *  `context` : clé de gabarit (bornes dures de la règle « width » et alerte de largeur, docs/15 §2.4 ; sans clé,
 *  union des familles du niveau, sans alerte) et référence des paires (`WidthContext`).
 *  Assez rapide pour tourner à chaque image pendant un geste. */
export function validate(
  corridor: unknown,
  bounds: Bounds | readonly number[] | null | undefined,
  level: string,
  context: WidthContext = {},
): CorridorCheck {
  const result = (rules: RuleCode[], warnings: string[], pairs: number[] = [], lines: LineName[] = []): CorridorCheck => ({
    rules,
    errors: rules.map((r) => RULES[r]),
    warnings,
    pairs,
    lines,
  });
  const c = parseCorridor(corridor);
  if (!c) return result(["format"], []);
  const structure = structureErrors(c, level);
  if (structure.length) return result(structure, []);
  const box = bounds ? ([Number(bounds[0]), Number(bounds[1]), Number(bounds[2]), Number(bounds[3])] as const) : null;
  const place = placeErrors(c, box);
  const warnings = widthWarnings(c, level, context);
  if (place.length) return result(place, warnings);
  const oriented = orient(c);
  const fault = railError(oriented, level, context.levelKey) ?? lineError(oriented);
  return fault ? result([fault.rule], warnings, fault.pairs, fault.lines) : result([], warnings);
}
